import { useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query'
import { createApiCall } from 'services'
import { SIGNUPS_QUERY_KEY } from './useSignups'

// VRG-247 (Waitlist pre-load, Batch 4b): pre-loads a subscription comp and/or
// activation-credit grant onto a Waitlist entry before its company exists.
// Applied automatically once a company is created for that signup.
type StagePendingSignupGrantFields = {
  // VRG-247 revision: whole-month durations 1-24, matching the comp DTO.
  compDurationMonths?: number
  compReason?: string
  activationAmount?: number
  activationReason?: string
}

/**
 * VRG-545 — keyed by an ALLOWLIST EMAIL or by a signup id, never both.
 *
 * A union rather than two optional fields: the two resolve through different
 * endpoints, and a payload carrying neither would compile and then 404 at
 * runtime. The allowlist table has no signup id to offer, and the address may
 * have no signups row at all — the API creates one (`status: 'granted'`).
 */
export type StagePendingSignupGrantPayload =
  | ({ signupId: string; email?: never } & StagePendingSignupGrantFields)
  | ({ email: string; signupId?: never } & StagePendingSignupGrantFields)

export type StagePendingSignupGrantResponse = {
  id: string
  pending_grants: Record<string, unknown> | null
}

export const useStagePendingSignupGrant = (
  options?: Omit<UseMutationOptions<StagePendingSignupGrantResponse, Error, StagePendingSignupGrantPayload>, 'mutationFn'>
) => {
  const queryClient = useQueryClient()

  return useMutation<StagePendingSignupGrantResponse, Error, StagePendingSignupGrantPayload>({
    mutationFn: async (payload) => {
      const { signupId, email, ...fields } = payload as StagePendingSignupGrantFields & {
        signupId?: string
        email?: string
      }
      const response = await createApiCall({
        method: 'POST',
        // Two routes, one action. The email one resolves the signups row
        // server-side (and creates it when the address was added straight to
        // the allowlist), so the client never has to know which case it is in.
        url: signupId
          ? `/internal/signups/${signupId}/pending-grants`
          : '/internal/signups/pending-grants-email',
        data: signupId ? fields : { ...fields, email },
      })
      if (response.status >= 400) {
        throw new Error(response.data?.message || 'Failed to stage grant')
      }
      return response.data as StagePendingSignupGrantResponse
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [SIGNUPS_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
    onError: options?.onError,
    ...options,
  })
}
